import React, { useState } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Badge,
    Image,
    Modal,
} from 'react-bootstrap';
import {
    PencilSquare,
    Download,
    Printer,
    CheckCircleFill,
    XCircleFill,
    EyeFill,
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { FILE_URL, API_URL } from '../../constants';
import { useTokens } from '../../context/TokenContext';

const VerifiedBadge = ({ verified }) =>
    verified ? (
        <CheckCircleFill className="text-success ms-2" title="Verified" />
    ) : (
        <XCircleFill className="text-danger ms-2" title="Not Verified" />
    );

const SectionCard = ({ title, verified, children }) => (
    <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-white fw-bold d-flex justify-content-between align-items-center">
            <span>{title}</span>
            <VerifiedBadge verified={verified} />
        </Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const VendorOverview = ({ vendorData, onEditProfile }) => {
    // Modal state for image preview
    const [previewUrl, setPreviewUrl] = useState(null);

    const { getItem } = useTokens();
    const vendorAuthToken = getItem('vendor', 'AuthToken');

    if (!vendorData) {
        return (
            <Container className="my-lg-5 my-4">
                <p className="text-center text-muted">No vendor data available.</p>
            </Container>
        );
    }

    // Destructure data for easier access - updated to match actual API response
    const {
        id,
        first_name,
        last_name,
        email,
        phone,
        uuid,
        createdOn,
        status,
        store_name,
        store_legal_name,
        address,
        city,
        zip,
        state,
        country,
        gst,
        fssai_number,
        documents,
        bankinfo_status,
        document_status,
        warehouses
    } = vendorData;

    // Use actual verification status from API
    const verification = {
        personal: status === 1, // Active status
        store: document_status === 'Verified',
        bank: bankinfo_status === 'Verified',
        documents: document_status === 'Verified',
    };

    const fullName = `${first_name || ''} ${last_name || ''}`.trim();

    // Helper for download
    const handleDownload = async (field) => {
        try {
            const response = await axios.get(
                `${API_URL}/download/file/${id}/${field}`,
                {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${vendorAuthToken}`,
                    },
                }
            );

            // Try to extract filename from Content-Disposition
            let filename = `${field}_document`;

            const disposition = response.headers['content-disposition'];
            if (disposition && disposition.includes('filename=')) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) {
                    filename = match[1];
                }
            }

            const blob = new Blob([response.data], {
                type: response.headers['content-type'],
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = filename;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);

            // Optional: user-friendly handling
            if (error.response?.status === 404) {
                alert('File not found');
            } else if (error.response?.status === 403) {
                alert('You are not authorized to download this file');
            } else {
                alert('Download failed');
            }
        }
    };

    // Helper for preview
    const handlePreview = (url) => {
        setPreviewUrl(FILE_URL ? FILE_URL + url : url);
    };

    // Section data
    const personalInfo = [
        ['Full Name', fullName],
        ['Email', email],
        ['Phone', phone],
        ['Address', `${address}, ${city}, ${zip}`],
        ['Country', country],
        ['Member Since', new Date(createdOn).toLocaleDateString()],
        ['UUID', uuid],
    ];

    const storeInfo = [
        ['Store Name', store_name],
        ['Store Legal Name', store_legal_name],
        ['GST', gst || 'N/A'],
    ];

    const bankInfo = [
        ['Account Holder Name', vendorData.accountHolderName || 'N/A'],
        ['Bank Name', vendorData.bankName || 'N/A'],
        ['Account Number', vendorData.accountNumber || 'N/A'],
        ['IFSC Code', vendorData.ifscCode || 'N/A'],
        ['Branch Name', vendorData.branchName || 'N/A'],
        ['UPI ID', vendorData.upiId || 'N/A'],
    ];

    const documentFiles = [
        {
            label: 'Brand Logo',
            url: documents?.brand_logo,
            field: 'brand_logo',
        },
        {
            label: 'Aadhaar Card',
            url: documents?.file_aadhar_card,
            field: 'file_aadhar_card',
        },
        {
            label: 'PAN Card',
            url: documents?.file_pan_card,
            field: 'file_pan_card',
        },
        {
            label: 'FSSAI Certificate',
            url: documents?.file_fssai_certificate,
            field: 'file_fssai_certificate',
        },
        {
            label: 'Bank Passbook',
            url: documents?.file_bank_passbook,
            field: 'file_bank_passbook',
        },
    ];

    return (
        <Container className="vendor-profile-container p-0">
            <Row className="mb-4 align-items-center border-bottom pb-3">
                <Col md={6} className="d-flex align-items-center">
                    <h5 className="mb-0 text-muted">
                        Vendor Profile <small className="text-secondary opacity-75">{uuid}</small>
                    </h5>
                </Col>
                <Col md={6} className="d-flex justify-content-end align-items-center">
                    <Badge bg={status === '1' ? 'success' : 'danger'} className="p-2 fs-6 me-3">
                        {status === '1' ? 'Active' : 'Inactive'}
                    </Badge>
                    <Link to={`/vendor/profile/edit-profile`} className="text-decoration-none">
                        <Button variant="primary" className="profile-edit-btn">
                            <PencilSquare size={18} className="me-1" /> Edit Profile
                        </Button>
                    </Link>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <SectionCard title="Personal Information" verified={verification.personal}>
                        <Table borderless size="sm">
                            <tbody>
                                {personalInfo.map(([label, value]) => (
                                    <tr key={label}>
                                        <td className="text-muted">{label}:</td>
                                        <td>{value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </SectionCard>

                    <SectionCard title="Store Information" verified={verification.store}>
                        <Table borderless size="sm">
                            <tbody>
                                {storeInfo.map(([label, value]) => (
                                    <tr key={label}>
                                        <td className="text-muted">{label}:</td>
                                        <td>{value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </SectionCard>

                    <SectionCard title="Bank Information" verified={verification.bank}>
                        <Table borderless size="sm">
                            <tbody>
                                {bankInfo.map(([label, value]) => (
                                    <tr key={label}>
                                        <td className="text-muted">{label}:</td>
                                        <td>{value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </SectionCard>

                    <SectionCard title="Documents" verified={verification.documents}>
                        <Row>
                            {documentFiles.map(
                                (doc) =>
                                    doc.url && (
                                        <Col md={6} className="mb-3" key={doc.label}>
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column align-items-center">
                                                    <div className="mb-2 fw-bold">{doc.label}</div>
                                                    <Image
                                                        src={FILE_URL ? FILE_URL + doc.url : doc.url}
                                                        alt={doc.label}
                                                        thumbnail
                                                        style={{ maxHeight: 120, objectFit: 'contain' }}
                                                    />
                                                    <div className="mt-2 d-flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => handleDownload(doc.field)}
                                                        >
                                                            <Download size={16} className="me-1" /> Download
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() => handlePreview(doc.url)}
                                                        >
                                                            <EyeFill size={16} className="me-1" /> Preview
                                                        </Button>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    )
                            )}
                        </Row>
                    </SectionCard>

                    {/* Warehouse Information */}
                    <SectionCard title="Warehouse Information" verified={warehouses && warehouses.length > 0}>
                        {warehouses && warehouses.length > 0 ? (
                            warehouses.map((warehouse, index) => (
                                <div key={warehouse.id} className={index > 0 ? 'mt-4 pt-3 border-top' : ''}>
                                    <h6 className="mb-3">Warehouse #{index + 1}</h6>
                                    <Table borderless size="sm">
                                        <tbody>
                                            <tr>
                                                <td className="text-muted">Name:</td>
                                                <td>{warehouse.name}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Email:</td>
                                                <td>{warehouse.email}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Phone:</td>
                                                <td>{warehouse.phone}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Address:</td>
                                                <td>{warehouse.address}{warehouse.address2 ? `, ${warehouse.address2}` : ''}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">City:</td>
                                                <td>{warehouse.city}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">State:</td>
                                                <td>{warehouse.state}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Pin Code:</td>
                                                <td>{warehouse.pinCode}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Country:</td>
                                                <td>{warehouse.country}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">GST:</td>
                                                <td>{warehouse.gstin || 'N/A'}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Status:</td>
                                                <td>
                                                    <Badge bg={warehouse.approvalStatus === 'approved' ? 'success' : 'warning'}>
                                                        {warehouse.approvalStatus}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            ))
                        ) : (
                            <p className="text-muted mb-0">No warehouse information available.</p>
                        )}
                    </SectionCard>
                </Col>
            </Row>

            {/* Fullscreen Preview Modal */}
            <Modal show={!!previewUrl} onHide={() => setPreviewUrl(null)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Document Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {previewUrl && (
                        <Image
                            src={previewUrl}
                            alt="Preview"
                            fluid
                            style={{ maxHeight: '80vh', objectFit: 'contain' }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default VendorOverview;